/** @format */

import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	Renderer2,
	inject,
	input
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

import { KillSwitches } from '@shared/data-access/interfaces';
import { ProductImagePictureSource } from '@pdp/utils';
import { Store as NgRxStore } from '@ngrx/store';
import { selectKillswitch } from '@pdp/data-access/state';

/** @format */

@Component({
	standalone: true,
	selector: 'pdp-product-image',
	templateUrl: './product-image.component.html',
	styleUrl: './product-image.component.scss',
	imports: [CommonModule]
})
export class ProductImageComponent implements OnInit, OnDestroy, AfterViewInit {
	pictureSources = input<ProductImagePictureSource[]>([]);
	mediaSetItemType = input.required<string>();
	killSwitches = input.required<KillSwitches>();
	chainIdentifierAbbr = input.required<string>();
	disableZoom = input(false);

	private readonly renderer = inject(Renderer2);
	private readonly el = inject(ElementRef);
	private readonly platformId = inject(PLATFORM_ID);
	ngRxStore = inject(NgRxStore);

	isImageGalleryZoomEnabled = this.ngRxStore.selectSignal(
		selectKillswitch('isImageGalleryZoomEnabled')
	);
	isVideoThumbnail = false;

	private unlistenMouseMove: () => void;
	private unlistenScroll: () => void;
	private unlistenMouseLeave: () => void;
	private unlistenMouseOver: () => void;

	private imageBoundingRectangle: DOMRect;
	private resizeObserver: ResizeObserver;
	private timeoutId: ReturnType<typeof setTimeout>;
	private shouldAttachZoomHandlers = false;

	private getTransformOriginForZoom(event: MouseEvent): string {
		const rect = this.imageBoundingRectangle;
		const x = event.clientX - rect.left;
		const percentageX = (x / rect.width) * 100;
		const y = event.clientY - rect.top;
		const percentageY = (y / rect.height) * 100;
		return `${percentageX}% ${percentageY}%`;
	}

	private attachImageZoomHandlers(): void {
		const image = this.el.nativeElement.querySelector('img') as HTMLImageElement;

		// Since we don't have access to the image in the template since it's a picture element, attach handler manually.
		if (image) {
			this.resizeObserver = new ResizeObserver(() => {
				this.imageBoundingRectangle = image.getBoundingClientRect();
			});

			// Need to also listen for scroll handler to re-update the bounding client rectangle
			// we use for image zooming CSS transition
			this.unlistenScroll = this.renderer.listen('window', 'scroll', () => {
				this.imageBoundingRectangle = image.getBoundingClientRect();
			});
			this.resizeObserver.observe(image);
			// Cache bounding rectangle call so it doesn't happen every mouse move for performance
			this.imageBoundingRectangle = image.getBoundingClientRect();

			this.unlistenMouseOver = this.renderer.listen(image, 'mouseover', (ev: MouseEvent) => {
				image.style.transformOrigin = this.getTransformOriginForZoom(ev);
				this.timeoutId = setTimeout(() => {
					this.renderer.addClass(image, 'zoomed');
				}, 500);
			});

			this.unlistenMouseLeave = this.renderer.listen(image, 'mouseleave', () => {
				this.renderer.removeClass(image, 'zoomed');
				clearTimeout(this.timeoutId);
			});

			this.unlistenMouseMove = this.renderer.listen(
				image,
				'mousemove',
				(event: MouseEvent) => {
					image.style.transformOrigin = this.getTransformOriginForZoom(event);
				}
			);
		}
	}

	ngOnInit(): void {
		if (isPlatformBrowser(this.platformId)) {
			this.isVideoThumbnail = this.mediaSetItemType() === 'video';
			this.shouldAttachZoomHandlers =
				this.isImageGalleryZoomEnabled() && !this.disableZoom() && !this.isVideoThumbnail;
		}
	}

	ngAfterViewInit(): void {
		if (isPlatformBrowser(this.platformId) && this.shouldAttachZoomHandlers) {
			this.attachImageZoomHandlers();
		}
	}

	ngOnDestroy(): void {
		if (this.unlistenMouseMove) {
			this.unlistenMouseMove();
		}

		if (this.unlistenScroll) {
			this.unlistenScroll();
		}

		if (this.unlistenMouseLeave) {
			this.unlistenMouseLeave();
		}

		if (this.unlistenMouseOver) {
			this.unlistenMouseOver();
		}

		this.resizeObserver?.disconnect();
	}
}
